import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ZoomIn, ZoomOut } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AuthImage } from "@/components/AuthImage";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const LEVELS = [1, 1.5, 2, 3];

/* Full-screen punch-selfie viewer for supervisors: dark immersive backdrop,
   zoom in/out controls, tap the photo to toggle 1x/2x, scroll to pan when
   zoomed. Zoom resets on close. When `comparePunchId` is given, the punched
   user's enrolled reference face is shown side-by-side for faster judgement. */
export const PhotoLightbox = ({ open, onOpenChange, photoId, title, subtitle, comparePunchId }) => {
  const { t } = useLang();
  const [zi, setZi] = useState(0);
  const zoom = LEVELS[zi];

  const { data: refData } = useQuery({
    queryKey: ["punch-reference", comparePunchId],
    queryFn: async () => (await api.get(`/punches/${comparePunchId}/reference`)).data,
    enabled: open && Boolean(comparePunchId),
    staleTime: 5 * 60 * 1000,
  });
  const referencePhotoId = refData?.referencePhotoId || null;

  const handleOpenChange = (v) => {
    if (!v) setZi(0);
    onOpenChange(v);
  };
  const toggleTap = () => setZi((i) => (i === 0 ? 2 : 0)); // 1x <-> 2x

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="photo-lightbox"
        aria-describedby={undefined}
        className="flex h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none border-0 bg-black/95 p-0 text-white backdrop-blur sm:rounded-none"
      >
        <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 pr-12">
          <div className="min-w-0">
            <DialogTitle className="truncate text-sm font-semibold text-white">
              {title}
            </DialogTitle>
            {subtitle && (
              <p className="truncate font-mono text-xs tabular-nums text-white/70">{subtitle}</p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="icon" variant="ghost" data-testid="photo-zoom-out"
              className="h-9 w-9 text-white hover:bg-white/15 hover:text-white"
              aria-label={t("zoomOut")} disabled={zi === 0}
              onClick={() => setZi((i) => Math.max(0, i - 1))}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span data-testid="photo-zoom-level"
              className="w-12 text-center font-mono text-xs tabular-nums text-white/80">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              size="icon" variant="ghost" data-testid="photo-zoom-in"
              className="h-9 w-9 text-white hover:bg-white/15 hover:text-white"
              aria-label={t("zoomIn")} disabled={zi === LEVELS.length - 1}
              onClick={() => setZi((i) => Math.min(LEVELS.length - 1, i + 1))}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden sm:flex-row">
          {referencePhotoId && (
            <div data-testid="lightbox-reference-pane"
              className="flex max-h-[30%] shrink-0 flex-col border-b border-white/10 p-2 sm:max-h-none sm:w-1/3 sm:border-b-0 sm:border-r">
              <p className="mb-1 text-center text-[11px] font-medium uppercase tracking-wide text-white/60">
                {t("enrolledReference")}
              </p>
              <div className="flex min-h-0 flex-1 items-center justify-center">
                <AuthImage
                  photoId={referencePhotoId}
                  thumb={false}
                  testId="lightbox-reference-photo"
                  className="max-h-full w-auto max-w-full rounded-lg object-contain"
                  alt="enrolled reference"
                />
              </div>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
            {referencePhotoId && (
              <p className="pt-2 text-center text-[11px] font-medium uppercase tracking-wide text-white/60">
                {t("punchSelfieLabel")}
              </p>
            )}
            <div
              style={zoom > 1 ? { width: `${zoom * 100}%` } : undefined}
              className="mx-auto flex min-h-full items-center justify-center p-3"
            >
              <button
                type="button"
                onClick={toggleTap}
                aria-label={zoom === 1 ? t("zoomIn") : t("zoomOut")}
                className={`block w-full ${zoom === 1 ? "cursor-zoom-in" : "cursor-zoom-out"}`}
                data-testid="photo-tap-zoom"
              >
                <AuthImage
                  photoId={photoId}
                  thumb={false}
                  testId="punch-photo-full"
                  className={
                    zoom === 1
                      ? "mx-auto max-h-[calc(100dvh-120px)] w-auto max-w-full rounded-lg object-contain"
                      : "h-auto w-full rounded-lg object-contain"
                  }
                />
              </button>
            </div>
          </div>
        </div>

        <p className="shrink-0 px-4 pb-3 pt-1 text-center text-[11px] text-white/50">
          {t("lightboxHint")}
        </p>
      </DialogContent>
    </Dialog>
  );
};
